import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Clock, MapPin } from "lucide-react";
import { HERO_IMAGE, scrollToId } from "@/data/content";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeVariants = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({
    opacity: 1,
    y: 0,
    transition: { delay: d, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  }),
};

const STATS = [
  { icon: MapPin, label: "20 Regioni coperte" },
  { icon: Clock, label: "Intervento in 24h" },
  { icon: ShieldCheck, label: "Personale D.Lgs 81/08" },
];

const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden bg-[#F9FAFB] pt-[72px]">
      <motion.div
        style={{ y: bgY }}
        className="pointer-events-none absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-[#00B36B]/[0.07] blur-3xl"
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-7">
          <motion.span
            variants={fadeVariants}
            initial="hidden"
            animate="show"
            custom={0.1}
            className="inline-flex items-center gap-2 rounded-full border border-[#00B36B]/25 bg-[#E8F8F0] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#009E5E]"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#00B36B]" />
            Impresa di pulizie · Tutta Italia
          </motion.span>

          <h1 className="mt-6 text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl lg:text-[4.2rem]">
            {["L'eccellenza", "del pulito,", "su tutta Italia."].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  custom={i}
                >
                  {i === 1 ? (
                    <>
                      <span className="font-serif-accent font-medium text-[#00B36B]">del pulito,</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            variants={fadeVariants}
            initial="hidden"
            animate="show"
            custom={0.75}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            Dagli uffici direzionali ai complessi industriali, dai condomini alle sanificazioni
            specializzate: Clario porta standard operativi puntuali e capillari in ogni regione
            italiana.
          </motion.p>

          <motion.div
            variants={fadeVariants}
            initial="hidden"
            animate="show"
            custom={0.9}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-quote-button"
              onClick={() => scrollToId("contatti")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#00B36B] px-7 py-4 text-sm font-bold text-white shadow-[0_12px_32px_rgba(0,179,107,0.35)] transition-all duration-300 hover:bg-[#009E5E] hover:-translate-y-0.5"
            >
              Richiedi Preventivo Gratuito
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-services-button"
              onClick={() => scrollToId("servizi")}
              className="rounded-full border border-slate-200 bg-white px-7 py-4 text-sm font-bold text-slate-700 transition-all duration-300 hover:border-[#00B36B]/40 hover:text-[#00B36B]"
            >
              Scopri i Servizi
            </button>
          </motion.div>

          <motion.div
            variants={fadeVariants}
            initial="hidden"
            animate="show"
            custom={1.05}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
          >
            {STATS.map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600">
                <Icon className="h-4 w-4 text-[#00B36B]" />
                {label}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:col-span-5"
        >
          <div className="relative overflow-hidden rounded-[2rem] shadow-[0_32px_80px_rgba(15,23,42,0.18)]">
            <motion.img
              style={{ y: imgY, scale: 1.15 }}
              src={HERO_IMAGE}
              alt="Spazi aziendali igienizzati e luminosi — Clario"
              className="h-[420px] w-full object-cover lg:h-[520px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/40 via-transparent to-transparent" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="absolute -bottom-6 -left-4 rounded-2xl border border-white/60 bg-white/80 px-5 py-4 shadow-xl backdrop-blur-xl sm:-left-8"
          >
            <p className="text-2xl font-extrabold text-slate-900">24h</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Attivazione sopralluogo
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
